import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Divider,
  Input,
  List,
  Modal,
  Row,
  Select,
  Typography,
  Form,
  message,
  Checkbox,
} from "antd";
import { ClearOutlined, EditOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../application/store";
import { loadOrders } from "../../../application/orders-slice";
import { useQuery } from "../../hooks";
import {
  OrderItem,
  OrdersRepository,
  OrderState,
  Transaction,
} from "../../../domain";
import EditOrderState from "./components/EditOrderState";
import DatePicker from "react-datepicker2";
import moment, { Moment } from "moment-jalaali";
import I18n from "../../i18n";
import { ServiceLocater, Services } from "../../../service-locater";

const { Text } = Typography;

export default function Orders() {
  const query = useQuery();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { loading, pageSize, ordersCount, orders } = useAppSelector(
    (state) => state.orders
  );
  const [modal, contextHolder] = Modal.useModal();

  const [orderStateModal, setOrderStateModal] = useState<{
    id?: string;
    currentState?: OrderState;
    visible: boolean;
  }>({ visible: false });
  const openOrderStateModal = (id: string, currentState: OrderState) =>
    setOrderStateModal({ visible: true, currentState, id });
  const closeOrderStateModal = () =>
    setOrderStateModal({
      visible: false,
      currentState: undefined,
      id: undefined,
    });

  const showItemsModal = (items: OrderItem[]) => {
    modal.info({
      direction: "rtl",
      centered: true,
      maskClosable: true,
      content: (
        <Fragment>
          {items.map((item) => {
            return (
              <Col>
                <Row>
                  <img
                    src={item.product.images[0].url}
                    alt={item.product.title}
                    width={100}
                  />
                  <Col style={{ marginRight: 16 }}>
                    <div>{item.product.title}</div>
                    <div>تعداد:‌ {item.count}</div>
                    <div>
                      {`رنگ:‌ ${item.attribute.color.title}`}
                      <div
                        style={{
                          marginRight: 8,
                          width: 12,
                          height: 12,
                          borderRadius: 6,
                          backgroundColor: item.attribute.color.color,
                          display: "inline-block",
                        }}
                      ></div>
                    </div>
                    <div>سایز:‌ {item.attribute.size}</div>
                    <div>
                      قیمت:‌ {item.attribute.price.toLocaleString()} تومان
                    </div>
                  </Col>
                </Row>
                <Divider />
              </Col>
            );
          })}
        </Fragment>
      ),
    });
  };

  const showTransactionModal = (transaction: Transaction) => {
    modal.info({
      direction: "rtl",
      centered: true,
      maskClosable: true,
      content: (
        <Row>
          <div>
            <Text>قیمت</Text>
            <br />
            <Text>نتیجه</Text>
            <br />
            <Text>کد تخفیف</Text>
          </div>
          <div style={{ marginRight: 16 }}>
            <Text>{transaction.amount.toLocaleString()} تومان</Text>
            <br />
            <Text>
              {transaction.result === "PENDING"
                ? "در انتظار بررسی"
                : transaction.result === "SUCCESSFUL"
                ? "موفق"
                : "ناموفق"}
            </Text>
            <br />
            <Text>{transaction.discountCode}</Text>
          </div>
        </Row>
      ),
    });
  };

  let page: number;
  try {
    const pageStr = query.get("page");
    page = parseInt(pageStr === null ? "1" : pageStr);
  } catch (_) {
    page = 1;
  }

  const startDate = query.get("startDate");
  const endDate = query.get("endDate");
  const state =
    query.get("state") === null
      ? "ALL"
      : (query.get("state") as OrderState | "ALL");
  const search =
    query.get("search") === null ? undefined : (query.get("search") as string);

  const [form] = Form.useForm();
  const [searchField, setSearchField] = useState<string | undefined>(search);

  const stableDispatch = useCallback(dispatch, []);
  useEffect(() => {
    stableDispatch(
      loadOrders(
        page,
        state,
        startDate !== null ? startDate : undefined,
        endDate !== null ? endDate : undefined,
        search
      )
    );
  }, [stableDispatch, page, state, startDate, endDate, search]);

  const sendOrderInfoSms = async (id: string) => {
    const hide = message.loading("در حال ارسال پیامک کد پیگیری...", 20);
    const res = await ServiceLocater.get<OrdersRepository>(
      Services.OrdersRepository
    ).sendOrderInfoSms(id);
    hide();
    res.fold(
      (l) => {
        message.error(I18n.general.networkError);
      },
      (r) => {
        message.success("پیامک با موفقیت ارسال شد.");
      }
    );
  };

  const getOrderState = (state: OrderState) => {
    if (state === "PENDING") return "در اتنظار پرداخت";
    if (state === "PAID") return "پرداخت شده";
    if (state === "POSTED") return "پست شده";
    return "تحویل شده";
  };

  const getOrderStateColor = (state: OrderState) => {
    if (state === "PENDING") return "#E0E0E0";
    if (state === "PAID") return "#FFF176";
    if (state === "POSTED") return "#81C784";
    return "#90CAF9";
  };

  const isLoading = loading !== "succeeded";
  return (
    <div>
      {contextHolder}
      <List
        style={{ paddingBottom: 8 }}
        dataSource={orders}
        loading={isLoading}
        header={
          <Col>
            <Row style={{ padding: "4px 16px" }} align="middle">
              <Typography.Text style={{ marginLeft: 8 }}>
                تاریخ شروع
              </Typography.Text>
              <DatePicker
                className={
                  "ant-input ant-input-lg ant-input-rtl" +
                  (isLoading ? " disbaled-datepicker" : "")
                }
                inputReadOnly={true}
                isGregorian={false}
                persianDigits={true}
                timePicker={false}
                value={startDate === null ? undefined : moment.utc(startDate)}
                onChange={(date: Moment) => {
                  if (date === undefined) return;
                  date.set({ hour: 0, minute: 0, second: 0 });
                  query.set("startDate", date.toISOString());
                  query.set("page", "1");
                  history.push({
                    pathname: "/orders",
                    search: query.toString(),
                  });
                }}
              />
              <Button
                type="link"
                size="small"
                onClick={() => {
                  query.delete("startDate");
                  query.set("page", "1");
                  history.push({
                    pathname: "/orders",
                    search: query.toString(),
                  });
                }}
              >
                <ClearOutlined />
              </Button>
              <Typography.Text style={{ marginRight: 24, marginLeft: 8 }}>
                تاریخ پایان
              </Typography.Text>
              <DatePicker
                className={
                  "ant-input ant-input-lg ant-input-rtl" +
                  (isLoading ? " disbaled-datepicker" : "")
                }
                inputReadOnly={true}
                isGregorian={false}
                persianDigits={true}
                timePicker={false}
                value={endDate === null ? undefined : moment.utc(endDate)}
                onChange={(date: Moment) => {
                  if (date === undefined) return;
                  date.set({ hour: 23, minute: 59, second: 59 });
                  query.set("endDate", date.toISOString());
                  query.set("page", "1");
                  history.push({
                    pathname: "/orders",
                    search: query.toString(),
                  });
                }}
              />
              <Button
                type="link"
                size="small"
                onClick={() => {
                  query.delete("endDate");
                  query.set("page", "1");
                  history.push({
                    pathname: "/orders",
                    search: query.toString(),
                  });
                }}
              >
                <ClearOutlined />
              </Button>
            </Row>
            <Row style={{ padding: "4px 16px" }} align="middle">
              <Typography.Text>{I18n.productsList.sortBy}:</Typography.Text>
              <Select
                size="small"
                defaultValue={state}
                dropdownMatchSelectWidth
                bordered={false}
                onSelect={(value, _) => {
                  query.set("state", value);
                  query.set("page", "1");
                  history.push({
                    pathname: location.pathname,
                    search: query.toString(),
                  });
                }}
              >
                <Select.Option value="ALL">همه‌ی حالات</Select.Option>
                <Select.Option value="PENDING">در انتظار پرداخت</Select.Option>
                <Select.Option value="PAID">پرداخت شده</Select.Option>
                <Select.Option value="POSTED">ارسال شده</Select.Option>
                <Select.Option value="DELIVERED">تحویل شده</Select.Option>
              </Select>
              <Form
                form={form}
                fields={[{ name: "search", value: searchField }]}
              >
                <Form.Item name="searchField" style={{ margin: 0 }}>
                  <Input
                    name="search"
                    maxLength={11}
                    disabled={isLoading}
                    allowClear
                    inputMode="numeric"
                    defaultValue={searchField}
                    onChange={(v) => {
                      const value = v.target.value;
                      const numberRegex = /^[0-9\b]+$/;
                      if (value === "" || numberRegex.test(value)) {
                        setSearchField(value);
                      }
                      form.setFields([
                        {
                          name: "search",
                          value: searchField ? searchField : "",
                        },
                      ]);
                    }}
                  />
                </Form.Item>
              </Form>
              <Button
                type="primary"
                style={{ marginRight: 8 }}
                onClick={() => {
                  if (searchField !== undefined) {
                    query.set("search", searchField);
                    query.set("page", "1");
                    history.push({
                      pathname: location.pathname,
                      search: query.toString(),
                    });
                  }
                }}
              >
                جستجو
              </Button>
            </Row>
          </Col>
        }
        renderItem={(item, index) => {
          return (
            <Card
              key={item.orderId}
              bodyStyle={{ padding: "0 8px" }}
              style={{ margin: "8px 16px" }}
              bordered
            >
              <List.Item
                actions={[
                  <Button
                    size="small"
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => openOrderStateModal(item.id, item.state)}
                  >
                    تغییر وضعیت
                  </Button>,
                  <Button
                    size="small"
                    type="link"
                    icon={
                      <span
                        style={{
                          marginLeft: 8,
                          height: 12,
                          width: 12,
                          borderRadius: 8,
                          backgroundColor:
                            item.transaction.result === "SUCCESSFUL"
                              ? "#4CAF50"
                              : "#F44336",
                        }}
                      />
                    }
                    disabled={item.transaction === undefined}
                    onClick={() => showTransactionModal(item.transaction)}
                  >
                    مشاهده تراکنش
                  </Button>,
                  <Button
                    type="link"
                    size="small"
                    onClick={() => showItemsModal(item.items)}
                  >
                    جزییات
                  </Button>,
                  <Button
                    type="link"
                    size="small"
                    disabled={item.postalTrackingCode === null}
                    onClick={() => sendOrderInfoSms(item.id)}
                  >
                    ارسال پیامک کدپیگیری
                  </Button>,
                ]}
              >
                <Col>
                  <div
                    style={{
                      display: "inline-block",
                      padding: "2px 4px",
                      borderRadius: 4,
                      backgroundColor: "#E3F2FD",
                      marginLeft: 8,
                    }}
                  >
                    کد سفارش: {item.orderId}
                  </div>
                  <div
                    style={{
                      display: "inline-block",
                      padding: "2px 4px",
                      borderRadius: 4,
                      backgroundColor: "#E3F2FD",
                    }}
                  >
                    {moment(item.date.toLocaleString()).format(
                      "HH:mm jYYYY/jMM/jDD"
                    )}
                  </div>
                  <div>{item.transaction.amount.toLocaleString()} تومان</div>
                  <div>{`${item.user.firstName} ${item.user.sureName} - ${item.user.phoneNumber}`}</div>
                  <div>{item.addressString}</div>
                  <div>
                    {item.state === "POSTED" || item.state === "DELIVERED"
                      ? "کد پیگیری پست:‌ " + item.postalTrackingCode
                      : ""}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <span
                      style={{
                        padding: "2px 4px",
                        borderRadius: 4,
                        backgroundColor: getOrderStateColor(item.state),
                        color: "#000",
                      }}
                    >
                      {getOrderState(item.state)}
                    </span>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      onChange={async (value) => {
                        const hide = message.loading("در حال ثبت...", 20);
                        const res = await ServiceLocater.get<OrdersRepository>(
                          Services.OrdersRepository
                        ).toggleIsPackaged(item.id);
                        hide();
                        res.fold(
                          (l) => {
                            message.error(I18n.general.networkError);
                            dispatch(
                              loadOrders(
                                page,
                                state,
                                startDate !== null ? startDate : undefined,
                                endDate !== null ? endDate : undefined,
                                search
                              )
                            );
                          },
                          (r) => {
                            message.success("با موفقیت ثبت شد.");
                          }
                        );
                      }}
                      defaultChecked={item.isPackaged}
                    >
                      بسته‌بندی شده
                    </Checkbox>
                  </div>
                </Col>
              </List.Item>
            </Card>
          );
        }}
        pagination={{
          pageSize,
          total: ordersCount,
          showSizeChanger: false,
          current: page,
          position: "bottom",
          style: { display: "table", margin: "0 auto" },
          onChange: (page, _) => {
            query.set("page", page.toString());
            history.push({
              pathname: location.pathname,
              search: query.toString(),
            });
          },
        }}
      />

      <EditOrderState
        visible={orderStateModal.visible}
        orderId={orderStateModal.id}
        currentState={orderStateModal.currentState}
        onClose={closeOrderStateModal}
        currentPage={page}
      />
    </div>
  );
}
